/**
 * Static route map component using Leaflet.
 * Renders a non-interactive map with the walk route polyline.
 * Uses CartoDB Positron No Labels (same as Camminata).
 */
import { useEffect, useRef } from 'react';

const TILE_URL = 'https://{s}.basemaps.cartocdn.com/light_nolabels/{z}/{x}/{y}{r}.png';

export default function RouteMap({ positions, className = '', height = 180 }) {
  const containerRef = useRef(null);
  const mapRef = useRef(null);

  useEffect(() => {
    if (!positions || positions.length < 2 || !containerRef.current) return;
    if (mapRef.current) return; // already initialized

    const initMap = () => {
      if (!window.L || !containerRef.current) return;
      
      try {
        const coords = positions.map(p => [p.lat, p.lng]);
        
        const map = window.L.map(containerRef.current, {
          zoomControl: false,
          attributionControl: false,
          dragging: false,
          touchZoom: false,
          doubleClickZoom: false,
          scrollWheelZoom: false,
          boxZoom: false,
          keyboard: false,
          tap: false
        });

        window.L.tileLayer(TILE_URL, {
          subdomains: 'abcd',
          maxZoom: 20
        }).addTo(map);

        const polyline = window.L.polyline(coords, {
          color: '#E8334A',
          weight: 4,
          opacity: 0.9
        }).addTo(map);

        // Start marker (green)
        window.L.circleMarker(coords[0], {
          radius: 6, fillColor: '#16A34A', fillOpacity: 1,
          color: '#fff', weight: 2
        }).addTo(map);

        // End marker (red)
        window.L.circleMarker(coords[coords.length - 1], {
          radius: 6, fillColor: '#E8334A', fillOpacity: 1,
          color: '#fff', weight: 2
        }).addTo(map);

        map.fitBounds(polyline.getBounds(), { padding: [20, 20] });
        mapRef.current = map;
      } catch (err) {
        console.warn('RouteMap init error:', err);
      }
    };

    // Ensure Leaflet is loaded
    if (window.L) {
      setTimeout(initMap, 50);
    } else {
      // Load Leaflet CSS
      if (!document.querySelector('link[href*="leaflet"]')) {
        const link = document.createElement('link');
        link.rel = 'stylesheet';
        link.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
        document.head.appendChild(link);
      }
      // Load Leaflet JS
      const script = document.createElement('script');
      script.src = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';
      script.onload = () => setTimeout(initMap, 50);
      document.head.appendChild(script);
    }

    return () => {
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
      }
    };
  }, [positions]);

  if (!positions || positions.length < 2) return null;

  return (
    <div 
      ref={containerRef} 
      className={`rounded-xl overflow-hidden ${className}`}
      style={{ height: `${height}px`, width: '100%' }}
    />
  );
}
